import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, Users, Gift, Calendar, Phone, Mail, Cake, Loader2, Euro, TrendingUp, Download, Tag } from "lucide-react";
import CustomerDetailDrawer from "@/components/CustomerDetailDrawer";

/** Salon-side customer list with aggregated stats. */
export default function CustomersManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ by: "last_booking_date", dir: "desc" });
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/cms/customers");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const customers = data?.customers || [];
  const showRevenue = !!data?.show_revenue;

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let arr = ql.length === 0
      ? customers
      : customers.filter((c) => [c.name, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(ql)));
    arr = [...arr].sort((a, b) => {
      const av = a[sort.by] ?? "";
      const bv = b[sort.by] ?? "";
      if (typeof av === "number" && typeof bv === "number") {
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      return sort.dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [customers, q, sort]);

  const totalRevenue = useMemo(() => customers.reduce((sum, c) => sum + (c.total_revenue || 0), 0), [customers]);
  const totalBookings = useMemo(() => customers.reduce((sum, c) => sum + (c.total_bookings || 0), 0), [customers]);
  const totalPoints = useMemo(() => customers.reduce((sum, c) => sum + (c.points_balance || 0), 0), [customers]);

  const toggleSort = (col) => {
    setSort((s) => s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "desc" });
  };

  const exportCsv = () => {
    const headers = ["Name", "E-Mail", "Telefon", "Geburtstag", "Treuepunkte", "Letzter Termin", "Letzte Leistung"];
    if (showRevenue) headers.push("Termine gesamt", "Umsatz gesamt (€)");
    const rows = filtered.map((c) => {
      const r = [c.name, c.email, c.phone, c.birthday || "", c.points_balance, c.last_booking_date || "", c.last_service_name || ""];
      if (showRevenue) r.push(c.total_bookings ?? 0, (c.total_revenue ?? 0).toFixed(2));
      return r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kunden_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (loading) return <div className="p-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="mv-label mb-2">CRM</p>
          <h1 className="font-display text-4xl">Kunden</h1>
          <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
            {customers.length} registrierte Kunden · {totalBookings} bestätigte/abgeschlossene Termine{showRevenue ? ` · ${totalRevenue.toFixed(2)} € Gesamtumsatz` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} disabled={filtered.length === 0} className="mv-btn-ghost text-sm" data-testid="customers-export-csv">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> CSV
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Kunden" value={customers.length} testId="kpi-customers" />
        <StatCard icon={Calendar} label="Termine" value={totalBookings} testId="kpi-bookings" />
        <StatCard icon={Gift} label="Treuepunkte (Σ)" value={totalPoints.toLocaleString("de-DE")} testId="kpi-points" />
        {showRevenue && <StatCard icon={Euro} label="Umsatz" value={`${totalRevenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`} testId="kpi-revenue" />}
      </div>

      <div className="mv-card overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--mv-border)" }}>
          <Search className="w-4 h-4 opacity-50" strokeWidth={1.5} />
          <input
            type="search"
            placeholder="Suche nach Name, E-Mail, Telefon…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="mv-input border-0 focus:ring-0 bg-transparent"
            data-testid="customers-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" strokeWidth={1.2} />
            <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>
              {q ? "Keine Treffer für die Suche." : "Noch keine registrierten Kunden."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead style={{ background: "var(--mv-surface)" }}>
                <tr className="text-xs uppercase tracking-wider" style={{ color: "var(--mv-text-muted)" }}>
                  <Th onClick={() => toggleSort("name")} active={sort.by === "name"} dir={sort.dir}>Name</Th>
                  <Th onClick={() => toggleSort("birthday")} active={sort.by === "birthday"} dir={sort.dir}>Geb. Datum</Th>
                  <Th>Kontakt</Th>
                  <Th onClick={() => toggleSort("points_balance")} active={sort.by === "points_balance"} dir={sort.dir} className="text-right">Treuepunkte</Th>
                  <Th onClick={() => toggleSort("last_booking_date")} active={sort.by === "last_booking_date"} dir={sort.dir}>Letzter Termin</Th>
                  <Th>Letzte Leistung</Th>
                  {showRevenue && (
                    <>
                      <Th onClick={() => toggleSort("total_bookings")} active={sort.by === "total_bookings"} dir={sort.dir} className="text-right">Termine Σ</Th>
                      <Th onClick={() => toggleSort("total_revenue")} active={sort.by === "total_revenue"} dir={sort.dir} className="text-right">Umsatz Σ</Th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className="border-t hover:bg-[var(--mv-surface)] transition-colors cursor-pointer"
                    style={{ borderColor: "var(--mv-border)" }}
                    data-testid={`customer-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium">{c.name || <span className="opacity-50 italic">(ohne Name)</span>}</p>
                      {(c.tags || []).length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {(c.tags || []).slice(0, 3).map((tg) => (
                            <span key={tg} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--mv-primary)", color: "#fff" }}>{tg}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.birthday ? (
                        <span className="inline-flex items-center gap-1 text-xs"><Cake className="w-3 h-3" strokeWidth={1.5} /> {formatDate(c.birthday)}</span>
                      ) : <span className="opacity-40 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="space-y-0.5 text-xs">
                        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:opacity-70 truncate max-w-[180px]"><Mail className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} /> {c.email}</a>}
                        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:opacity-70"><Phone className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} /> {c.phone}</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.points_balance > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                          <Gift className="w-3 h-3" strokeWidth={1.6} /> {c.points_balance}
                        </span>
                      ) : <span className="opacity-40 text-xs">0</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.last_booking_date ? formatDate(c.last_booking_date) + (c.last_booking_time ? ` · ${c.last_booking_time}` : "") : <span className="opacity-40">noch keiner</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">{c.last_service_name || <span className="opacity-40">—</span>}</td>
                    {showRevenue && (
                      <>
                        <td className="px-4 py-3 text-right tabular-nums">{c.total_bookings ?? 0}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">{(c.total_revenue ?? 0).toFixed(2)} €</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!showRevenue && (
        <p className="text-xs mt-3 italic" style={{ color: "var(--mv-text-muted)" }}>
          <TrendingUp className="w-3 h-3 inline-block mr-1" strokeWidth={1.5} />
          Umsatz und Gesamttermine sind dem Salon-Admin vorbehalten.
        </p>
      )}

      <CustomerDetailDrawer
        open={!!activeId}
        customerId={activeId}
        onClose={() => setActiveId(null)}
        onUpdated={load}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="mv-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="mv-label">{label}</p>
        <Icon className="w-3.5 h-3.5 opacity-40" strokeWidth={1.5} />
      </div>
      <p className="text-2xl font-display">{value}</p>
    </div>
  );
}

function Th({ children, onClick, active, dir, className = "" }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 text-left font-medium ${onClick ? "cursor-pointer select-none" : ""} ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span className="text-[10px]">{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}
